import type { RequestFn } from '../http';
import type { CWReference, KanbanFilters, KanbanResponse, KanbanTechnician } from '../types';

type KanbanAllFilters = Omit<KanbanFilters, 'page' | 'pageSize'>;

export function createKanbanApi(request: RequestFn) {
  return {
    async getKanbanTickets(filters: KanbanFilters = {}) {
      const params = new URLSearchParams();
      if (filters.boardId) params.set('boardId', String(filters.boardId));
      if (filters.statusIds && filters.statusIds.length > 0) {
        params.set('statusIds', filters.statusIds.join(','));
      }
      if (filters.companyId) params.set('companyId', String(filters.companyId));
      if (filters.search) params.set('search', filters.search);
      if (filters.includeClosed !== undefined) {
        params.set('includeClosed', String(filters.includeClosed));
      }
      if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
      if (filters.page) params.set('page', String(filters.page));

      const query = params.toString() ? `?${params.toString()}` : '';
      return request<KanbanResponse>(`/cw/kanban/tickets${query}`);
    },

    async getAllKanbanTickets(filters: KanbanAllFilters = {}) {
      const params = new URLSearchParams();
      if (filters.boardId) params.set('boardId', String(filters.boardId));
      if (filters.statusIds && filters.statusIds.length > 0) {
        params.set('statusIds', filters.statusIds.join(','));
      }
      if (filters.companyId) params.set('companyId', String(filters.companyId));
      if (filters.search) params.set('search', filters.search);
      if (filters.includeClosed !== undefined) {
        params.set('includeClosed', String(filters.includeClosed));
      }
      const query = params.toString() ? `?${params.toString()}` : '';
      return request<KanbanResponse>(`/cw/kanban/all-tickets${query}`);
    },

    async getKanbanTechnicians() {
      return request<{ technicians: KanbanTechnician[] }>('/cw/kanban/technicians');
    },

    async getKanbanBoards() {
      return request<{ boards: CWReference[] }>('/cw/kanban/boards');
    },

    async getKanbanBoardStatuses(boardId: number) {
      return request<{ statuses: CWReference[] }>(`/cw/kanban/boards/${boardId}/statuses`);
    },

    async getKanbanPriorities() {
      return request<{ priorities: CWReference[] }>('/cw/kanban/priorities');
    },

    async clearKanbanCache() {
      return request<{ success: boolean; message: string }>('/cw/kanban/cache/clear', {
        method: 'POST',
      });
    },

    async updateKanbanTicketOwner(
      ticketId: number,
      memberIdentifier: string | null,
      reasons: {
        /** Pflichtbegründung beim Entfernen des Owners (min. 10 Zeichen). */
        releaseReason?: string;
        /** Pflichtbegründung, wenn ein TEAMLEAD einer anderen Person zuweist. */
        assignReason?: string;
      } = {},
    ) {
      return request<{ success: boolean }>(`/cw/kanban/tickets/${ticketId}/owner`, {
        method: 'PATCH',
        body: JSON.stringify({ memberIdentifier, ...reasons }),
      });
    },
  };
}
